import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../UI/dialog';
import { Button } from '../UI/button';
import { Label } from '../UI/label';
import { ArrowLeft, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import CustomSearchableDropdown from '../Common/CustomSearchableDD';
import { useSelector } from 'react-redux';

const InquiryCloseDialog = ({
  open,
  onOpenChange,
  onConfirm,
  rejectionReasonOptions = [],
}) => {
  const [selectedResult, setSelectedResult] = useState("");
  const [createOrderFromInquiry, setCreateOrderFromInquiry] = useState(false);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState("");

  // Reset states when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedResult("");
      setCreateOrderFromInquiry(false);
      setSelectedRejectionReason("");
    }
  }, [open]);

  const handleCancel = () => {
    setSelectedResult("");
    setCreateOrderFromInquiry(false);
    setSelectedRejectionReason("");
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (!selectedResult) {
      toast.error("Please select a result option.");
      return;
    }

    if (selectedResult === "rejected") {
      if (!selectedRejectionReason) {
        toast.error("Please select a rejection reason.");
        return;
      }
      // Call onConfirm with rejection action and reason
      onConfirm("rejected", selectedRejectionReason);
    } else if (selectedResult === "converted") {
      // Call onConfirm with converted action and order creation flag - direct operation
      onConfirm("converted", null, createOrderFromInquiry);
    }

    // Reset states
    setSelectedResult("");
    setCreateOrderFromInquiry(false);
    setSelectedRejectionReason("");
    onOpenChange(false);
  };

  const handleResultToggle = (result) => {
    if (selectedResult === result) {
      setSelectedResult("");
    } else {
      setSelectedResult(result);
      // Reset other options when switching
      if (result === "converted") {
        setSelectedRejectionReason("");
      } else if (result === "rejected") {
        setCreateOrderFromInquiry(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="sm:max-w-md w-full"
      >
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          {/* Mobile: Back Arrow with Title */}
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose
                tabIndex={-1}
                className="p-0 w-auto h-auto flex-shrink-0 flex justify-center"
                onClick={handleCancel}
              >
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">
                  Close Inquiry
                </div>
              </DialogTitle>
            </div>
          </div>

          {/* Desktop: Title */}
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            Close Inquiry
          </DialogTitle>

          {/* Desktop: Close X */}
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose
              className="flex-shrink-0"
              tabIndex={-1}
              onClick={handleCancel}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>
        <div className="px-4 flex-1 overflow-y-auto max-h-[70vh]">
          <div className="space-y-4">
            <div>
              <Label className="font-medium mb-3 block">What was the result?</Label>

            <div className="space-y-3">
                {/* Converted Successfully Option */}
                <div
                  onClick={() => handleResultToggle("converted")}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md border transition-all duration-200 cursor-pointer
                    ${selectedResult === "converted" ? 'bg-sahi-green text-white border-sahi-green' : 'bg-white text-sahi-green border-gray-300 hover:border-sahi-green hover:bg-gray-50'}
                  `}
                >
                  {selectedResult === "converted" }
                  <span>Converted Successfully</span>
                </div>

                {/* Rejected Option */}
                <div
                  onClick={() => handleResultToggle("rejected")}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md border transition-all duration-200 cursor-pointer
                    ${selectedResult === "rejected" ? 'bg-sahi-red text-white border-sahi-red' : 'bg-white text-sahi-red border-gray-300 hover:border-sahi-red hover:bg-gray-50'}
                  `}
                >
                  {selectedResult === "rejected" }
                  <span>Rejected</span>
                </div>
              </div>
            </div>

            {selectedResult === "converted" && (
              <div className="bg-gray-50 border border-sahi-border-color rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createOrderFromInquiry}
                    onChange={(e) => setCreateOrderFromInquiry(e.target.checked)}
                    className="mt-1 h-4 w-4 text-sahi-green border-gray-300 sahi-radius focus:ring-sahi-green"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-color">Create Sale from Inquiry</div>
                  </div>
                </label>
              </div>
            )}

            {/* Rejection Reason dropdown - only show when rejected is selected */}
            {selectedResult === "rejected" && rejectionReasonOptions.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="rejection-reason" className="font-medium">
                  Failure Reason
                </Label>
                <CustomSearchableDropdown
                  options={rejectionReasonOptions}
                  value={selectedRejectionReason}
                  onSelect={setSelectedRejectionReason}
                  placeholder="Select Reason"
                  displayKey="label"
                  valueKey="value"
                  label="Failure Reason"
                  emptyMessage="No failure reasons found"
                  searchEnabled={true}
                />
              </div>
            )}
          </div>

          <div className="flex gap-2 py-2 my-2">
            <Button onClick={handleConfirm}>
              <Check className="h-4 w-4" />
              Close Inquiry
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InquiryCloseDialog;